import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { formatBankName } from '../../bank-movements/utils/formatters';

const COMPANY_COLORS = {
  headerBg: '5e894f',
  headerFont: 'FFFFFF',
  evenRow: 'E3E6F3',
  oddRow: 'FFFFFF',
  positiveGreen: '4caf50',
  negativeRed: 'f44336',
};

export async function exportProjectedReportToExcel(
  report,
  selectedBanks = [],
  filename = 'Informe_Proyectado.xlsx'
) {
  if (!report || !report.periods || report.periods.length === 0) return;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Informe Proyectado');

  const allBanks = new Set();
  report.periods.forEach((period) => {
    period.banks?.forEach((bank) => allBanks.add(bank.bank_name));
  });
  const sortedAllBanks = Array.from(allBanks).sort();
  const banksToShow =
    selectedBanks.length > 0
      ? sortedAllBanks.filter((bank) => selectedBanks.includes(bank))
      : sortedAllBanks;

  const columns = [
    { header: 'Periodo', key: 'period', width: 40 },
  ];

  banksToShow.forEach((bankName) => {
    columns.push({
      header: `${formatBankName(bankName)} - Saldo Periodo`,
      key: `${bankName}_periodo`,
      width: 18,
    });
    columns.push({
      header: `${formatBankName(bankName)} - Libro`,
      key: `${bankName}_libro`,
      width: 18,
    });
  });

  columns.push({ header: 'Total Neto', key: 'total_net', width: 16 });
  columns.push({ header: 'Total Cierre', key: 'total_closing', width: 16 });

  worksheet.columns = columns;

  worksheet.getRow(1).eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COMPANY_COLORS.headerBg },
    };
    cell.font = {
      color: { argb: COMPANY_COLORS.headerFont },
      bold: true,
      size: 11,
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  report.periods.forEach((period, idx) => {
    const rowData = {
      period: period.label || `Periodo ${period.period_index}`,
    };

    const bankMap = {};
    (period.banks || []).forEach((bank) => {
      bankMap[bank.bank_name] = bank;
    });

    banksToShow.forEach((bankName) => {
      const bankData = bankMap[bankName];
      rowData[`${bankName}_periodo`] = bankData?.net_movement || 0;
      rowData[`${bankName}_libro`] = bankData?.closing_balance || 0;
    });

    rowData.total_net = period.totals?.net_movement || 0;
    rowData.total_closing = period.totals?.closing_balance || 0;

    const row = worksheet.addRow(rowData);

    row.eachCell((cell, colNumber) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: idx % 2 === 0 ? COMPANY_COLORS.evenRow : COMPANY_COLORS.oddRow },
      };

      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };

      if (colNumber > 1) {
        cell.numFmt = '[$$-es-AR] #,##0.00';

        const key = columns[colNumber - 1]?.key;
        if (key && (key.endsWith('_periodo') || key === 'total_net')) {
          const value = cell.value;
          if (typeof value === 'number') {
            if (value >= 0) {
              cell.font = { color: { argb: COMPANY_COLORS.positiveGreen }, bold: true };
            } else {
              cell.font = { color: { argb: COMPANY_COLORS.negativeRed }, bold: true };
            }
          }
        }
      }

      if (cell.value === null || cell.value === undefined || cell.value === '') {
        cell.value = '';
      }
    });
  });

  worksheet.getRow(1).height = 30;

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename
  );
}
